// apps/admin/src/routes/Dashboard.tsx
import { useQuery } from "convex/react";
import { useTranslation } from "react-i18next";
import { api } from "@jourfx/convex/client";
import {
  Building2,
  Users,
  Key,
  AlertTriangle,
  TrendingUp,
  Clock,
  ShieldAlert,
  ChevronRight,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Alert,
  AlertTitle,
  AlertDescription,
  Skeleton,
} from "@jourfx/ui";
import { PageHeader } from "../components/PageHeader";
import { SystemStatusCard } from "../components/SystemStatusCard";

// Mock data for development when not authenticated
const mockStats = {
  organizations: { total: 0, active: 0, trial: 0 },
  users: { total: 0, active: 0, pending: 0 },
  licenses: { active: 0, expiring: 0, expired: 0 },
  recentActivity: [],
};

export function Dashboard() {
  const { t } = useTranslation("dashboard");
  const { t: tc } = useTranslation("common");

  const stats = useQuery(api.admin.stats.get);

  // Handle loading state
  if (stats === undefined) {
    return <DashboardSkeleton />;
  }

  // Handle auth error - show login prompt
  if (stats === null) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-warning-muted">
          <ShieldAlert className="h-8 w-8 text-warning" />
        </div>
        <h2 className="text-xl font-semibold text-foreground">
          {tc("auth.loginRequired")}
        </h2>
        <p className="text-muted-foreground text-center max-w-md">
          {tc("auth.loginRequiredMessage")}
        </p>
        <Button asChild>
          <Link to="/login">{tc("auth.toLogin")}</Link>
        </Button>
      </div>
    );
  }

  // Use mock data as fallback
  const data = stats || mockStats;
  const showSuperAdminQuickActions = data.adminRole === "super_admin";

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={t("title")}
        description={t("subtitle")}
      />

      {/* Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title={t("stats.organizations")}
          value={data.organizations.total}
          subtext={t("statsSubtext.orgStatus", {
            active: data.organizations.active,
            trial: data.organizations.trial,
          })}
          icon={Building2}
          variant="primary"
        />
        <StatCard
          title={t("stats.users")}
          value={data.users.total}
          subtext={t("statsSubtext.userStatus", {
            active: data.users.active,
            pending: data.users.pending,
          })}
          icon={Users}
          variant="success"
        />
        <StatCard
          title={t("stats.activeLicenses")}
          value={data.licenses.active}
          subtext={t("statsSubtext.expiringLicenses", {
            count: data.licenses.expiring,
          })}
          icon={Key}
          variant="info"
          warning={data.licenses.expiring > 0}
        />
        <StatCard
          title={t("stats.expiredLicenses")}
          value={data.licenses.expired}
          subtext={t("statsSubtext.renewalRequired")}
          icon={Clock}
          variant="warning"
          warning={data.licenses.expired > 0}
        />
      </div>

      {/* System Status Card */}
      <SystemStatusCard />

      {/* Alerts Section */}
      {(data.licenses.expiring > 0 || data.licenses.expired > 0) && (
        <Alert variant="warning">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t("alerts.actionRequired")}</AlertTitle>
          <AlertDescription>
            <ul className="mt-2 space-y-1 text-sm">
              {data.licenses.expiring > 0 && (
                <li>
                  • {t("alerts.licensesExpiring", { count: data.licenses.expiring })}
                </li>
              )}
              {data.licenses.expired > 0 && (
                <li>
                  • {t("alerts.licensesExpired", { count: data.licenses.expired })}
                </li>
              )}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {showSuperAdminQuickActions && (
        <div className="grid gap-4 md:grid-cols-3">
          <QuickActionCard
            title={t("quickActions.addOrganization.title")}
            description={t("quickActions.addOrganization.description")}
            href="/organizations/new"
            icon={Building2}
          />
          <QuickActionCard
            title={t("quickActions.manageLicenses.title")}
            description={t("quickActions.manageLicenses.description")}
            href="/licenses"
            icon={Key}
          />
          <QuickActionCard
            title={t("quickActions.viewAuditLogs.title")}
            description={t("quickActions.viewAuditLogs.description")}
            href="/audit"
            icon={TrendingUp}
          />
        </div>
      )}

      {/* Recent Activity */}
      <Card>
        <CardHeader>
          <CardTitle>{t("recentActivity.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          {data.recentActivity.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {t("recentActivity.empty")}
            </p>
          ) : (
            <div className="space-y-4">
              {data.recentActivity.map(
                (activity: {
                  id: string;
                  message: string;
                  userName: string;
                  time: string;
                }) => (
                  <div
                    key={activity.id}
                    className="flex items-start space-x-3 text-sm"
                  >
                    <div className="mt-1.5 h-2 w-2 rounded-full bg-primary" />
                    <div className="flex-1 space-y-1">
                      <p className="text-foreground leading-none">
                        {activity.message}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {activity.userName} • {activity.time}
                      </p>
                    </div>
                  </div>
                )
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Skeleton loader for dashboard
function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header skeleton */}
      <div className="space-y-2">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-64" />
      </div>

      {/* Stats grid skeleton */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <Skeleton className="h-10 w-10 rounded-lg" />
              </div>
              <div className="mt-4 space-y-2">
                <Skeleton className="h-8 w-16" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-32" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick actions skeleton */}
      <div className="grid gap-4 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <Card key={i}>
            <CardContent className="flex items-center space-x-4 p-4">
              <Skeleton className="h-12 w-12 rounded-lg" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-40" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent activity skeleton */}
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-36" />
        </CardHeader>
        <CardContent className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="flex items-start space-x-3">
              <Skeleton className="mt-1.5 h-2 w-2 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-full max-w-xs" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Stat card component with design system colors
 * Per /DESIGN.md:
 * - Primary: Teal (#0891B2)
 * - Success: Emerald
 * - Warning: Amber
 * - Info: Sky
 */
type StatVariant = "primary" | "success" | "info" | "warning";

const variantStyles: Record<
  StatVariant,
  { bg: string; text: string; icon: string }
> = {
  primary: {
    bg: "bg-primary/10",
    text: "text-primary",
    icon: "text-primary",
  },
  success: {
    bg: "bg-success-muted",
    text: "text-success-muted-foreground",
    icon: "text-success",
  },
  info: {
    bg: "bg-info-muted",
    text: "text-info-muted-foreground",
    icon: "text-info",
  },
  warning: {
    bg: "bg-warning-muted",
    text: "text-warning-muted-foreground",
    icon: "text-warning",
  },
};

function StatCard({
  title,
  value,
  subtext,
  icon: Icon,
  variant,
  warning,
}: {
  title: string;
  value: number;
  subtext: string;
  icon: React.ComponentType<{ className?: string }>;
  variant: StatVariant;
  warning?: boolean;
}) {
  const { t } = useTranslation("dashboard");
  const styles = variantStyles[variant];

  return (
    <Card className="transition-all hover:shadow-md hover:border-primary/20">
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className={`flex h-12 w-12 items-center justify-center rounded-xl ${styles.bg}`}>
            <Icon className={`h-6 w-6 ${styles.icon}`} />
          </div>
          {warning && (
            <div className="flex items-center gap-1.5 text-warning">
              <AlertTriangle className="h-4 w-4" />
              <span className="text-xs font-medium">{t("actionRequired")}</span>
            </div>
          )}
        </div>
        <div className="mt-4 space-y-1">
          <p className="text-3xl font-bold tabular-nums tracking-tight text-foreground">
            {value.toLocaleString()}
          </p>
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-xs text-muted-foreground/80">{subtext}</p>
        </div>
      </CardContent>
    </Card>
  );
}

// Quick action card component
function QuickActionCard({
  title,
  description,
  href,
  icon: Icon,
}: {
  title: string;
  description: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Card className="group transition-all hover:shadow-md hover:border-primary/20">
      <Link to={href} className="block">
        <CardContent className="flex items-center space-x-4 p-4">
          <div className="rounded-lg bg-secondary p-3 transition-colors group-hover:bg-primary/10">
            <Icon className="h-6 w-6 text-muted-foreground transition-colors group-hover:text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-foreground group-hover:text-primary transition-colors">
              {title}
            </p>
            <p className="text-sm text-muted-foreground truncate">
              {description}
            </p>
          </div>
          <ChevronRight className="h-5 w-5 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5 group-hover:text-primary" />
        </CardContent>
      </Link>
    </Card>
  );
}
